import React, { useEffect, useState } from 'react';

import * as api from './UserReplacementApi';
import * as notifications from 'lib/notifications';
import Timestamp from 'components/Common/Timestamp';
import UserDtoView from 'components/Common/UserDtoView';
import { UserReplacementRequest, UserReplacementViewModel } from 'models/UserReplacement';
import { PermissionType, UserDto } from 'models/index';

import useClient from 'components/Files/useClient';

import './UserReplacement.css';
import Select from 'react-select';

// Bootstrap
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';

// react-hook-form
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

// zod
import * as z from 'zod';

import { format, compareAsc, isValid } from 'date-fns'
import FmtDate from 'components/Common/FmtDate';
import { dateToInputDate, isValidDate } from 'lib/DateTimeUtils';

interface IUserReplacementFormProps {
    users: UserDto[];
    editedItem: UserReplacementRequest;
    cancel: () => void;
    ok: () => void;
}

const validationSchema = z.object({
    userId: z.number().nullable(),
    replacingUserId: z.number().min(1, { message: 'Required' }),
    fromDate: z.date().nullable(),
    toDate: z.date().nullable(),
});

function UserReplacementForm(props: IUserReplacementFormProps) {
    const cancel = () => props.cancel();
    
    const { userHasPermission } = useClient();

    const {
        handleSubmit,
        formState: { errors },
        control,
        watch
    } = useForm<UserReplacementRequest>({
        resolver: zodResolver(validationSchema),
        //defaultValues: props.editedItem,
        values: props.editedItem,
        resetOptions: {
            keepDirtyValues: true, // user-interacted input will be retained
            keepErrors: true, // input errors will be retained with value update
        }
    });

    const onSubmit = async (data: UserReplacementRequest) => {
        console.log("onSubmit", data);
        try {
            await api.userReplacementPost(data);
            notifications.dispatch("Data sparades", 3);
            props.ok();
        } catch (e) {
            notifications.dispatchFromError("Fel vid sparande", e, 5);
        }
    };

    const allFields = watch();
    console.log("selectedOptions", allFields);
    console.log("errors", errors);

    return (
        <>
            <Modal show={true} onHide={cancel} size='lg'>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Ny ersättare</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {userHasPermission(PermissionType.Admin) && (
                        <Form.Group className={`mb-3 ${errors.userId ? 'is-invalid' : ''}`} controlId="formUserId" >
                            <Form.Label>Ersättare</Form.Label>

                            <Controller
                                control={control}
                                name="userId"
                                render={({
                                    field: { onChange, onBlur, value: formFieldValue, name, ref },
                                    fieldState: { invalid, isTouched, isDirty, error },
                                    formState,
                                }) => {
                                    console.log("error", error);
                                    return (
                                        <Select
                                            value={props.users?.find((u) => u.id == formFieldValue)}
                                            onChange={(newValue) => onChange(newValue.id)}
                                            options={props.users}
                                            getOptionLabel={val => `${val.fullName} (${val.email})`}
                                            //formatOptionLabel={(data, x) => {return <div style={{display: "flex", justifyContent: "space-between"}}><div>{data.fullName}</div><div>{data.email}</div></div>}}
                                            getOptionValue={val => val.id.toFixed()}
                                            className='react-select'
                                        />
                                    );
                                }}
                            />

                            {errors.userId && (
                                <Form.Control.Feedback type="invalid" className='d-block'>Detta fält är obligatoriskt.</Form.Control.Feedback>
                            )}
                        </Form.Group>
                        )}
                        <Form.Group className={`mb-3 ${errors.replacingUserId ? 'is-invalid' : ''}`} controlId="formReplacingUserId" >
                            <Form.Label>Ersättare</Form.Label>

                            <Controller
                                control={control}
                                name="replacingUserId"
                                render={({
                                    field: { onChange, onBlur, value: formFieldValue, name, ref },
                                    fieldState: { invalid, isTouched, isDirty, error },
                                    formState,
                                }) => {
                                    console.log("error", error);
                                    return (
                                        <Select
                                            value={props.users?.find((u) => u.id == formFieldValue)}
                                            onChange={(newValue) => onChange(newValue.id)}
                                            options={props.users}
                                            getOptionLabel={val => `${val.fullName} (${val.email})`}
                                            //formatOptionLabel={(data, x) => {return <div style={{display: "flex", justifyContent: "space-between"}}><div>{data.fullName}</div><div>{data.email}</div></div>}}
                                            getOptionValue={val => val.id.toFixed()}
                                            className='react-select'
                                        />
                                    );
                                }}
                            />

                            {errors.replacingUserId && (
                                <Form.Control.Feedback type="invalid" className='d-block'>Detta fält är obligatoriskt.</Form.Control.Feedback>
                            )}
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formFromDate">
                            <Form.Label>Från och med datum</Form.Label>

                            <Controller control={control} name="fromDate"
                                defaultValue={null}
                                render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <Form.Control
                                        onChange={(raw) => {
                                            const tmp = raw.target.value;
                                            const newValue = new Date(tmp);
                                            if (!isValidDate(newValue))
                                                return onChange(null);
                                            return onChange(newValue);
                                        }}
                                        value={dateToInputDate(value) || ""}
                                        type="date"
                                        className={`${errors.fromDate ? 'is-invalid' : ''}`}
                                    />
                                )}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formToDate">
                            <Form.Label>Till och med datum</Form.Label>
                            <Controller control={control} name="toDate"
                                defaultValue={null}
                                render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <Form.Control
                                        onChange={(raw) => {
                                            const tmp = raw.target.value;
                                            const newValue = new Date(tmp);
                                            if (!isValidDate(newValue))
                                                return onChange(null);
                                            return onChange(newValue);
                                        }}
                                        value={dateToInputDate(value) || ""}
                                        type="date"
                                        className={`${errors.toDate ? 'is-invalid' : ''}`}
                                    />
                                )}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={cancel}>
                            Stäng
                        </Button>
                        <Button variant="primary" type="submit">
                            Spara
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>

    )
}

interface IAppProps {
    userId: number;
}

function UserReplacementContainer(props: IAppProps) {
    const { userHasPermission, currentUser } = useClient();

    //console.log("AttachedFilesContainer");
    const [users, setUsers] = useState<UserDto[]>(null);
    const [items, setItems] = useState<UserReplacementViewModel[]>(null);

    useEffect(() => {
        // Update the document title using the browser API
        refreshData();
        api.userReplacementUserList().then(data => {
            //console.log("got data", data);
            setUsers(data);
        }).catch((reason) => {
            //console.log("catch", reason);
            notifications.dispatchFromError("Fel vid hämtning av användare", reason, 5);
        });
    }, []);

    function newItem(): UserReplacementRequest {
        const result = {
            givenNr: null, 
            userId: currentUser.id, 
            replacingUserId: null,
            fromDate: null,
            //new Date("2023-05-07"), 
            toDate: null
            //new Date("2023-05-08") 
        } as UserReplacementRequest;
        return result;
    }

    function addItem() {
        setEditedItem(newItem());
    }
    const [editedItem, setEditedItem] = useState<UserReplacementRequest>(); //newItem());

    function canEdit(vm: UserReplacementViewModel) {
        return vm.user?.id == props.userId 
                || userHasPermission(PermissionType.Admin);
    }

    return (
        <div className="row">
            <div className="col-sm-12">

                {editedItem && (
                    <UserReplacementForm users={users} editedItem={editedItem} 
                    cancel={() => setEditedItem(null)} 
                    ok={() => {setEditedItem(null); refreshData();}}
                    />
                )}

                <fieldset>
                    <legend>Ersättare           <Button variant="link" className='float-end' onClick={() => { addItem(); }}>Lägg till</Button>
                    </legend>

                    <Table striped hover responsive>
                        <thead>
                            <tr>
                                <th>Nr</th>
                                <th>Skapad</th>
                                <th>Användare</th>
                                <th>Ersättare</th>
                                <th>Från</th>
                                <th>Till</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items && items.length < 1 && ("Inga rader hittades.")}
                            {items && items.map((value, index) => (
                                <tr key={value.givenNr}>
                                    <td>
                                        {value.givenNr}
                                    </td>
                                    <td><Timestamp value={value.created} /></td>
                                    <td><UserDtoView value={value.user} /></td>
                                    <td><UserDtoView value={value.replacingUser} /></td>
                                    <td><FmtDate value={value.fromDate} /></td>
                                    <td><FmtDate value={value.toDate} /></td>
                                    <td>
                                        {canEdit(value) &&
                                            <>
                                                &nbsp;
                                                <a href="#" onClick={() => { deleteItem(value); return false; }}>Ta bort</a>      
                                            </>
                                        }
                                    </td>
                                </tr>
                            ))}
                        </tbody>

                    </Table>
                </fieldset>
            </div>
        </div>

    );

    function refreshData() {
        api.userReplacementList().then(data => {
            //console.log("got data", data);
            setItems(data);
        }).catch((reason) => {
            //console.log("catch", reason);
            notifications.dispatchFromError("Fel vid hämtning av data", reason, 5);
        });
    }

    function deleteItem(value: UserReplacementViewModel) {
        //console.log("deleteFile");
        api.userReplacementDelete(value).then(data => {
            //console.log("got data", data);
            notifications.dispatch("Raden togs bort", 3);
            refreshData();
        }).catch((reason) => {
            //console.log("catch", reason);
            notifications.dispatchFromError("Fel vid borttagning", reason);
        });
    }
}

export default UserReplacementContainer;
